// --- React ---
import { useState, useEffect } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";

// --- Services ---
import standardSvc from "../../services/api/standard";

// --- Models ---
import { StandardItem, StandardFilter } from "../../models/types/standard";
import { DefaultFilterRequest, PaginationMeta } from "../../models/types/common";

// --- Components ---
import ListTable from "../../components/list-table";
import Pagination from "../../components/pagination";
import { notify } from "../../common/notificationService";
import ButtonAction from "../../components/button/buttonAction";
import { UrlCollection } from "../../common/url-collection";
import ModalConfirm from "../../components/modal-comfirm";
import MenuItem from "@mui/material/MenuItem";
import ButtonMore from "../../components/button/buttonMore";
import StandardFilterView from "./filter";

// --- Utils ---
import dayjs from "dayjs";
import { useResponsive } from "../../hooks/mediaQuery";

// --- Icons ---
import { Trash2, Pencil, Eye, ListMinus } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function StandardPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { isXs, isLg, isXl } = useResponsive();

    const [data, setData] = useState<StandardItem[]>([]);
    const [pagination, setPagination] = useState<PaginationMeta>({});
    const [orderBy, setOrderBy] = useState<string>("modifiedDate");
    const [order, setOrder] = useState<"asc" | "desc">("desc");
    const [loading, setLoading] = useState<boolean>(false);
    const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<StandardItem | null>(null);

    const getListAll = async (params: DefaultFilterRequest<StandardFilter>) => {
        setLoading(true);
        try {
            const res = await standardSvc.getAll(params);
            if (res && res.content) {
                const { items, pageCount, totalItemCount, pageIndex, pageSize } = res.content;
                setData(items);
                setPagination({ pageCount, totalItemCount, pageIndex, pageSize });
            }
        } catch (error) {
            console.error(error);
            setData([]);
            setPagination({});
            notify(t('common.error'), t('common.errorMessage'), "danger");
        } finally {
            setLoading(false);
        }
    };

    const onDelete = async (row: StandardItem) => {
        try {
            const res = await standardSvc.deleteStandard(row.id);
            if (res && res.content.status) {
                notify(t('common.success'), t('standard.notifications.deleteSuccess'), "success");
                fetchData();
            }
        } catch (error) {
            console.error(error);
            notify(t('common.error'), t('common.errorMessage'), "danger");
        }
    };

    const fetchData = async () => {
        const queryParams = new URLSearchParams(location.search);
        const issuedDateFromTo = queryParams.get("issuedDateFromTo");
        const effectiveDateFromTo = queryParams.get("effectiveDateFromTo");
        const endDateFromTo = queryParams.get("endDateFromTo");
        const params: DefaultFilterRequest<StandardFilter> = {
            sorting: queryParams.get("sorting"),
            pageIndex: queryParams.get("pageIndex") || "1",
            pageSize: queryParams.get("pageSize") || "20",
            name: queryParams.get("name"),
            description: queryParams.get("description"),
            isDelete: queryParams.get("isDelete") || "false",
            issuedDateFrom: issuedDateFromTo ? issuedDateFromTo.split(",")[0] : null,
            issuedDateTo: issuedDateFromTo ? issuedDateFromTo.split(",")[1] : null,
            effectiveDateFrom: effectiveDateFromTo ? effectiveDateFromTo.split(",")[0] : null,
            effectiveDateTo: effectiveDateFromTo ? effectiveDateFromTo.split(",")[1] : null,
            endDateFrom: endDateFromTo ? endDateFromTo.split(",")[0] : null,
            endDateTo: endDateFromTo ? endDateFromTo.split(",")[1] : null,
        };

        await getListAll(params);
    };

    useEffect(() => {
        fetchData();
    }, [location.search]);

    const handlePageChange = (page: number) => {
        searchParams.set("pageIndex", page.toString());
        setSearchParams(searchParams);
    };

    const handleChangePageSize = (size: number) => {
        searchParams.set("pageSize", size.toString());
        searchParams.set("pageIndex", "1"); // reset về trang đầu tiên
        setSearchParams(searchParams);
    };

    const onRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === "asc";
        const sort = isAsc ? "desc" : "asc";
        setOrder(sort);
        setOrderBy(property);

        searchParams.set("sorting", `${property} ${sort}`);
        setSearchParams(searchParams);
    };

    const handleEdit = (row: StandardItem) => {
        navigate(UrlCollection.EditStandard.replace(":id", row.id.toString()));
    };

    const handleDelete = (row: StandardItem) => {
        setSelectedRow(row);
        setOpenModalConfirm(true);
    };

    const columns = [
        { header: t('standard.table.stt'), accessor: "index", sortable: false, className: "w-[50px]" },
        { header: t('standard.table.name'), accessor: "name", className: "min-w-[100px]" },
        { header: t('standard.table.description'), accessor: "description", sortable: false, className: "min-w-[150px]" },
        {
            header: t('standard.table.issuedDate'),
            accessor: (row: StandardItem) => `${row.issuedDate ? dayjs(row.issuedDate).format("DD/MM/YYYY") : '-'}`,
            sortable: false,
            sortBy: 'issuedDate',
            className: "min-w-[90px]",
        },
        {
            header: t('standard.table.effectiveDate'),
            accessor: (row: StandardItem) => `${row.effectiveDate ? dayjs(row.effectiveDate).format("DD/MM/YYYY") : '-'}`,
            sortable: false,
            sortBy: 'effectiveDate',
            className: "min-w-[90px]",
        },
        {
            header: t('standard.table.endDate'),
            accessor: (row: StandardItem) => `${row.endDate ? dayjs(row.endDate).format("DD/MM/YYYY") : '-'}`,
            sortable: false,
            sortBy: 'endDate',
            className: "min-w-[90px]",
        },
        {
            header: "",
            accessor: (row: StandardItem) => {
                if (isXl || isLg) {
                    return (
                        <div className="flex gap-2">
                            <ButtonAction
                                onClick={() => navigate(UrlCollection.ViewStandard.replace(":id", row.id.toString()))}
                                icon={<Eye size={14} />}
                                color="blue"
                                title={t('standard.actions.view')}
                            />

                            <ButtonAction
                                onClick={() => handleEdit(row)}
                                icon={<Pencil size={14} />}
                                color="blue"
                                title={t('standard.actions.edit')}
                            />

                            <ButtonAction
                                onClick={() => handleDelete(row)}
                                icon={<Trash2 size={14} />}
                                color="red"
                                title={t('standard.actions.delete')}
                            />
                        </div>
                    )
                }
                else {
                    return (
                        <ButtonMore
                            icon={<ListMinus size={14} />}
                            title="More"
                        >
                            <MenuItem onClick={() => navigate(UrlCollection.ViewStandard.replace(":id", row.id.toString()))}>
                                <Eye size={14} color="blue" className="mr-2" />
                                <span>{t('standard.actions.view')}</span>
                            </MenuItem>
                            <MenuItem onClick={() => handleEdit(row)}>
                                <Pencil size={14} color="blue" className="mr-2" />
                                <span>{t('standard.actions.edit')}</span>
                            </MenuItem>
                            <MenuItem onClick={() => handleDelete(row)}>
                                <Trash2 size={14} color="red" className="mr-2" />
                                <span>{t('standard.actions.delete')}</span>
                            </MenuItem>
                        </ButtonMore>
                    )
                }
            },
            className: "max-w-[50px]"
        }
    ];

    return (
        <div className="page-wrapper list flex flex-col">
            <div className="page-header">
                <div className="flex items-center gap-2">
                    <h1 className="text-xl font-semibold">{t('standard.title')}</h1>
                </div>
                <button
                    className="btn shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={() => navigate(UrlCollection.CreateStandard)}
                >
                    {t('standard.addNew')}
                </button>
            </div>
            <div className={`flex ${isXs ? 'my-1' : 'my-2'}`}>
                <StandardFilterView />
            </div>
            <ListTable
                columns={columns}
                data={data}
                isLoading={loading}
                sortBy={orderBy}
                order={order}
                handleSort={onRequestSort}
            />
            {pagination.totalItemCount ? (
                <Pagination
                    pageCount={pagination.pageCount}
                    totalItemCount={pagination.totalItemCount}
                    pageIndex={pagination.pageIndex}
                    pageSize={pagination.pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handleChangePageSize}
                />
            ) : null}
            {openModalConfirm && (
                <ModalConfirm
                    isOpen={openModalConfirm}
                    onClose={() => setOpenModalConfirm(false)}
                    title={t('common.confirm')}
                    content={t('standard.modal.confirmDelete.content', { name: selectedRow?.name })}
                    titleComfirm={t('common.confirm')}
                    action={() => {
                        onDelete(selectedRow!);
                        setOpenModalConfirm(false);
                    }}
                />
            )}
        </div>
    );
}
